import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/AppContext";

const blueLight = "#0D6EFD";
const blue = "#0A3D91";
const dark1 = "#00264d";
const dark2 = "#000814";
const black = "#050816";
const white = "#ffffff";
const muted = "#64748b";
const border = "#e5e7eb";

const money = (value) => `$ ${Number(value || 0).toFixed(2)}`;

const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      py: 1.25,
      borderBottom: `1px solid ${border}`,
      "&:last-child": {
        borderBottom: "none",
      },
    }}
  >
    <Typography
      sx={{
        color: muted,
        fontSize: "0.74rem",
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        color: black,
        fontSize: "0.95rem",
        fontWeight: 800,
        wordBreak: "break-word",
      }}
    >
      {value || "No registrado"}
    </Typography>
  </Box>
);

const SummaryCard = ({ label, value, helper }) => (
  <Paper
    elevation={0}
    sx={{
      height: "100%",
      borderRadius: "18px",
      border: `1px solid ${border}`,
      backgroundColor: white,
      p: 2,
      boxShadow: "none",
    }}
  >
    <Typography
      sx={{
        color: muted,
        fontSize: "0.74rem",
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        mb: 0.6,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        color: black,
        fontSize: { xs: "1.25rem", md: "1.45rem" },
        fontWeight: 950,
        lineHeight: 1.05,
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>

    <Typography
      sx={{
        color: muted,
        fontSize: "0.78rem",
        fontWeight: 600,
        mt: 0.7,
        lineHeight: 1.45,
      }}
    >
      {helper}
    </Typography>
  </Paper>
);

const SectionHeader = ({ title, subtitle, dark = false }) => (
  <Box
    sx={{
      px: { xs: 2.2, md: 2.8 },
      py: 2,
      background: dark ? `linear-gradient(135deg, ${blueLight}, ${blue})` : "#f8fafc",
      borderBottom: dark ? "none" : `1px solid ${border}`,
      color: dark ? white : black,
    }}
  >
    <Typography sx={{ fontSize: "1.08rem", fontWeight: 950 }}>
      {title}
    </Typography>

    {subtitle && (
      <Typography
        sx={{
          color: dark ? "#dbeafe" : muted,
          fontSize: "0.86rem",
          mt: 0.4,
          fontWeight: 600,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const UserPanel = () => {
  const navigate = useNavigate();

  const {
    user,
    activeAccount,
    authLoading,
  } = useContext(AppContext);

  const [dashboard, setDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);

  useEffect(() => {
    document.title = "Mi Cuenta | CMSOLTEC";
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (!user?.isLoggedIn || user?.accountType !== "client") {
      navigate("/login", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const personaFallback = activeAccount?.user || {};

  const nombreFallback =
    personaFallback.nombre ||
    personaFallback.username ||
    personaFallback.razon_social ||
    personaFallback.nombre_comercial ||
    user?.username ||
    user?.name ||
    "Cliente";

  const correoFallback =
    personaFallback.email ||
    personaFallback.correo ||
    user?.email ||
    user?.correo ||
    "";

  const identificacionFallback =
    personaFallback.identificador ||
    personaFallback.identificacion ||
    personaFallback.documento ||
    personaFallback.ruc ||
    personaFallback.cedula ||
    user?.identificador ||
    user?.identificacion ||
    user?.documento ||
    user?.ruc ||
    user?.cedula ||
    "";

  useEffect(() => {
    const loadDashboard = async () => {
      if (authLoading) return;
      if (!user?.isLoggedIn || user?.accountType !== "client") return;

      setLoadingDashboard(true);

      try {
        const token = activeAccount?.access || localStorage.getItem("access") || "";

        const response = await fetch("/clientes/dashboard-publico/", {
          method: "GET",
          headers: {
            "Cache-Control": "no-store",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(correoFallback ? { "X-Cliente-Correo": correoFallback } : {}),
            ...(identificacionFallback ? { "X-Cliente-Identificacion": identificacionFallback } : {}),
          },
        });

        const data = await response.json().catch(() => ({}));

        if (response.ok && data.ok) {
          setDashboard(data);
        } else {
          setDashboard(null);
        }
      } catch {
        setDashboard(null);
      } finally {
        setLoadingDashboard(false);
      }
    };

    loadDashboard();
  }, [
    authLoading,
    user?.isLoggedIn,
    user?.accountType,
    activeAccount?.access,
    correoFallback,
    identificacionFallback,
  ]);

  const persona = dashboard?.persona || {};

  const nombre =
    persona.nombre_comercial ||
    persona.razon_social ||
    nombreFallback;

  const correo =
    persona.correo ||
    correoFallback;

  const identificacion =
    persona.ruc ||
    persona.cedula ||
    identificacionFallback;

  const resumen = dashboard?.resumen || {};
  const documentos = dashboard?.documentos || [];
  const pagos = dashboard?.pagos || [];
  const descuentos = dashboard?.descuentos || {};
  const descuentosActivos = descuentos.activos || [];
  const descuentosInactivos = descuentos.inactivos || [];
  const descuentosTodos = descuentos.todos || [];

  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: blue }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #ffffff 0%, #f6f9ff 48%, #edf5ff 100%)",
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              color: black,
              fontSize: { xs: "1.85rem", md: "2.4rem" },
              fontWeight: 950,
              lineHeight: 1.05,
              letterSpacing: "-0.045em",
            }}
          >
            Mi Cuenta
          </Typography>
        </Box>

        <Grid container spacing={2.2}>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "22px",
                border: `1px solid ${border}`,
                backgroundColor: white,
                overflow: "hidden",
                boxShadow: "none",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  p: { xs: 2.5, md: 3 },
                  background: `linear-gradient(135deg, ${dark1}, ${dark2})`,
                  color: white,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.25rem", md: "1.48rem" },
                    fontWeight: 950,
                    lineHeight: 1.15,
                    wordBreak: "break-word",
                  }}
                >
                  {nombre}
                </Typography>

                <Typography
                  sx={{
                    color: "#dbeafe",
                    fontSize: "0.92rem",
                    mt: 0.7,
                    wordBreak: "break-word",
                  }}
                >
                  {correo || "Correo no registrado"}
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 2.2, md: 2.5 } }}>
                <Stack>
                  <InfoRow label="Cliente" value={nombre} />
                  <InfoRow label="Correo" value={correo} />
                  <InfoRow label="Identificación" value={identificacion} />
                </Stack>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <SummaryCard
                  label="Documentos"
                  value={loadingDashboard ? "..." : resumen.total_documentos || 0}
                  helper="Documentos emitidos a tu nombre."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <SummaryCard
                  label="Total facturado"
                  value={loadingDashboard ? "..." : money(resumen.total_facturado)}
                  helper="Total registrado en documentos."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <SummaryCard
                  label="Pagado"
                  value={loadingDashboard ? "..." : money(resumen.total_pagado)}
                  helper="Pagos aplicados a tus documentos."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <SummaryCard
                  label="Saldo pendiente"
                  value={loadingDashboard ? "..." : money(resumen.total_saldo || resumen.deuda_cartera)}
                  helper="Valor pendiente según cartera."
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "22px",
                border: `1px solid ${border}`,
                backgroundColor: white,
                boxShadow: "none",
                overflow: "hidden",
              }}
            >
              <SectionHeader
                title="Descuentos disponibles"
                subtitle="Secciones y categorías donde tienes descuentos activos."
                dark
              />

              <Box sx={{ p: { xs: 2.2, md: 2.6 } }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <SummaryCard
                      label="Descuentos activos"
                      value={loadingDashboard ? "..." : descuentos.total_activos || descuentosActivos.length || 0}
                      helper="Aplican según vigencia y estado."
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <SummaryCard
                      label="Total registrados"
                      value={loadingDashboard ? "..." : descuentos.total || descuentosTodos.length || 0}
                      helper="Historial de descuentos asignados."
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <SummaryCard
                      label="Inactivos o vencidos"
                      value={loadingDashboard ? "..." : descuentos.total_inactivos || descuentosInactivos.length || 0}
                      helper="No aplican actualmente."
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 2.5,
                    border: `1px solid ${border}`,
                    borderRadius: "16px",
                    overflow: "hidden",
                  }}
                >
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 900, color: black }}>Tipo</TableCell>
                          <TableCell sx={{ fontWeight: 900, color: black }}>Aplica en</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900, color: black }}>Descuento</TableCell>
                          <TableCell sx={{ fontWeight: 900, color: black }}>Vigencia</TableCell>
                          <TableCell sx={{ fontWeight: 900, color: black }}>Estado</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {descuentosTodos.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography
                                sx={{
                                  color: muted,
                                  py: 2.5,
                                  textAlign: "center",
                                  fontWeight: 700,
                                  fontSize: "0.9rem",
                                }}
                              >
                                No tienes descuentos registrados.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          descuentosTodos.map((d) => (
                            <TableRow key={d.id} hover>
                              <TableCell sx={{ fontWeight: 800 }}>
                                {d.tipo_label || (d.tipo === "CATEGORIA" ? "Categoría" : "Sección")}
                              </TableCell>

                              <TableCell>
                                <Typography sx={{ fontWeight: 900, color: blue, fontSize: "0.88rem" }}>
                                  {d.nombre_descuento || d.category_name || d.section_name || "-"}
                                </Typography>

                                {d.tipo === "CATEGORIA" && d.section_name && (
                                  <Typography sx={{ color: muted, fontSize: "0.78rem", mt: 0.2 }}>
                                    Sección: {d.section_name}
                                  </Typography>
                                )}
                              </TableCell>

                              <TableCell align="right" sx={{ fontWeight: 950, color: blue }}>
                                {Number(d.porcentaje || 0).toFixed(2)}%
                              </TableCell>

                              <TableCell sx={{ fontWeight: 700 }}>
                                {d.vigencia || "Siempre"}
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{
                                    display: "inline-flex",
                                    px: 1.2,
                                    py: 0.35,
                                    borderRadius: "999px",
                                    fontSize: "0.74rem",
                                    fontWeight: 900,
                                    color: d.aplica_hoy ? "#166534" : "#92400e",
                                    backgroundColor: d.aplica_hoy ? "#dcfce7" : "#fef3c7",
                                  }}
                                >
                                  {d.aplica_hoy ? "Activo" : "No aplica hoy"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "22px",
                border: `1px solid ${border}`,
                backgroundColor: white,
                boxShadow: "none",
                overflow: "hidden",
              }}
            >
              <SectionHeader
                title="Documentos recientes"
                subtitle="Últimos documentos registrados con tu identificación."
                dark
              />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 900, color: black }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 900, color: black }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 900, color: black }}>Documento</TableCell>
                      <TableCell sx={{ fontWeight: 900, color: black }}>Estado</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: black }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: black }}>Saldo</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {documentos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography
                            sx={{
                              color: muted,
                              py: 2.5,
                              textAlign: "center",
                              fontWeight: 700,
                              fontSize: "0.9rem",
                            }}
                          >
                            No hay documentos registrados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      documentos.slice(0, 10).map((doc) => (
                        <TableRow key={`${doc.tipo_documento}-${doc.id}`} hover>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {doc.fecha_emision || "-"}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 800 }}>
                            {doc.tipo_documento_label || "-"}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 900, color: blue }}>
                            {doc.numero_documento || "-"}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 700 }}>
                            {doc.estado || doc.sri_estado || "-"}
                          </TableCell>

                          <TableCell align="right" sx={{ fontWeight: 800 }}>
                            {money(doc.total)}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 900,
                              color: Number(doc.saldo || 0) > 0 ? "#b91c1c" : "#166534",
                            }}
                          >
                            {money(doc.saldo)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "22px",
                border: `1px solid ${border}`,
                backgroundColor: white,
                boxShadow: "none",
                overflow: "hidden",
              }}
            >
              <SectionHeader
                title="Pagos recientes"
                subtitle="Últimos pagos aplicados a tus documentos."
              />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 900, color: black }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 900, color: black }}>Documento</TableCell>
                      <TableCell sx={{ fontWeight: 900, color: black }}>Forma</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: black }}>Valor</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pagos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography
                            sx={{
                              color: muted,
                              py: 2.5,
                              textAlign: "center",
                              fontWeight: 700,
                              fontSize: "0.9rem",
                            }}
                          >
                            No hay pagos registrados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagos.slice(0, 8).map((pago, index) => (
                        <TableRow key={`${pago.id}-${index}`} hover>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {pago.fecha || "-"}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 900, color: blue }}>
                            {pago.documento_numero || "-"}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 700 }}>
                            {pago.forma || "-"}
                          </TableCell>

                          <TableCell align="right" sx={{ fontWeight: 900, color: "#166534" }}>
                            {money(pago.valor)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default UserPanel;